import { useState, type FormEvent } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, Shield, UserPlus, X } from "lucide-react";

import { AdminTopbar } from "@/components/admin/AdminTopbar";
import { AdminCard, ListState } from "@/components/admin/primitives";
import { formatDateTime, relativeDays } from "@/lib/dates";
import {
  useInviteMember,
  useRemoveMember,
  useTeam,
  useUpdateMember,
  type TeamMember,
  type TeamRole,
  type TeamStatus,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

/**
 * Equipe e papeis.
 *
 * Dois papeis apenas: Administrador ve tudo e mexe em catalogo, precos e
 * equipe; Atendente so trabalha pedidos e clientes. Nao ha cadastro aberto —
 * uma conta do painel so nasce de um convite feito aqui.
 */
export const Route = createFileRoute("/admin/_painel/equipe/")({
  head: () => ({ meta: [{ title: "Equipe — Painel MonteBox" }] }),
  component: AdminEquipe,
});

const STATUS: Record<TeamStatus, { label: string; dot: string; text: string }> = {
  ATIVO: { label: "Ativo", dot: "bg-emerald-500", text: "text-mb-ink" },
  CONVITE_PENDENTE: {
    label: "Convite pendente",
    dot: "bg-mb-yellow",
    text: "text-mb-muted",
  },
  DESATIVADO: { label: "Acesso desativado", dot: "bg-mb-line", text: "text-mb-muted" },
};

const ROLE_LABEL: Record<TeamRole, string> = {
  ADMIN: "Administrador",
  ATENDENTE: "Atendente",
  REVENDEDOR: "Revendedor",
};

const COLUMNS = "min-w-[960px] grid grid-cols-[320px_180px_180px_180px_1fr] items-center gap-4";

function AdminEquipe() {
  const team = useTeam();
  const invite = useInviteMember();
  const update = useUpdateMember();
  const remove = useRemoveMember();
  const [inviting, setInviting] = useState(false);

  const members = team.data ?? [];

  const changeRole = (member: TeamMember) => {
    const role: TeamRole = member.role === "ADMIN" ? "ATENDENTE" : "ADMIN";
    update.mutate(
      { id: member.id, role },
      {
        onSuccess: () => toast.success(`${member.name} agora é ${ROLE_LABEL[role]}.`),
        onError: (cause) => toast.error(message(cause)),
      },
    );
  };

  const changeStatus = (member: TeamMember, status: TeamStatus) => {
    update.mutate(
      { id: member.id, status },
      {
        onSuccess: () =>
          toast.success(
            status === "DESATIVADO"
              ? `Acesso de ${member.name} desativado.`
              : `${member.name} reativado.`,
          ),
        onError: (cause) => toast.error(message(cause)),
      },
    );
  };

  return (
    <>
      <AdminTopbar
        title="Equipe"
        actions={
          <button
            type="button"
            onClick={() => setInviting(true)}
            className="flex items-center gap-1.5 rounded-full bg-mb-navy px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-mb-ink"
          >
            <UserPlus className="h-4 w-4" />
            Convidar pessoa
          </button>
        }
      />

      <div className="flex flex-col gap-6 p-4 sm:p-8">
        <div className="flex items-start gap-3 rounded-mb-card border border-mb-line bg-white px-5 py-4 shadow-card">
          <Shield className="mt-0.5 h-[18px] w-[18px] shrink-0 text-mb-navy" />
          <div>
            <p className="text-sm font-bold text-mb-ink">Dois papéis</p>
            <p className="mt-0.5 text-sm leading-5 text-mb-muted text-pretty">
              <strong className="font-bold text-mb-ink">Administrador</strong> vê tudo e mexe no
              catálogo, nos preços e na equipe.{" "}
              <strong className="font-bold text-mb-ink">Atendente</strong> só trabalha os pedidos e
              os clientes — não altera preço nem convida ninguém.
            </p>
          </div>
        </div>

        {inviting && <InviteForm onClose={() => setInviting(false)} invite={invite} />}

        <AdminCard className="overflow-x-auto">
          <div className={`${COLUMNS} border-b border-mb-line bg-mb-surface px-6 py-3`}>
            {["Pessoa", "Papel", "Status", "Último acesso"].map((head) => (
              <span
                key={head}
                className="text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted"
              >
                {head}
              </span>
            ))}
            <span className="text-right text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted">
              Ações
            </span>
          </div>

          <ListState
            loading={team.isLoading}
            error={team.error}
            empty={members.length === 0}
            emptyLabel="Nenhuma pessoa na equipe."
          />

          {members.map((member) => {
            const status = STATUS[member.status];

            return (
              <div
                key={member.id}
                className={`${COLUMNS} border-b border-mb-line px-6 py-3.5 last:border-b-0`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-mb-surface font-display text-[13px] font-bold text-mb-navy">
                    {initials(member.name)}
                  </span>
                  <div className="min-w-0">
                    <Link
                      to="/admin/equipe/$membroId"
                      params={{ membroId: member.id }}
                      className="block truncate text-sm font-bold text-mb-ink hover:text-mb-navy"
                    >
                      {member.name}
                      {member.isMe && <span className="font-semibold text-mb-muted"> · você</span>}
                    </Link>
                    <p className="truncate text-[11px] leading-4 text-mb-muted">{member.email}</p>
                  </div>
                </div>

                <span className="text-sm text-mb-muted">{ROLE_LABEL[member.role]}</span>

                <span className={cn("flex items-center gap-2 text-sm", status.text)}>
                  <span className={cn("h-2 w-2 rounded-full", status.dot)} />
                  {status.label}
                </span>

                <span
                  className="text-sm text-mb-muted"
                  title={member.lastLoginAt ? formatDateTime(member.lastLoginAt) : undefined}
                >
                  {member.lastLoginAt ? relativeDays(member.lastLoginAt) : "Nunca acessou"}
                </span>

                <div className="flex items-center justify-self-end gap-2">
                  {member.isMe ? (
                    <span className="text-sm text-mb-muted">—</span>
                  ) : (
                    <>
                      {member.role !== "REVENDEDOR" && (
                        <button
                          type="button"
                          disabled={update.isPending}
                          onClick={() => changeRole(member)}
                          className="rounded-full border border-mb-line px-3.5 py-[7px] text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface disabled:opacity-50"
                        >
                          Tornar {ROLE_LABEL[member.role === "ADMIN" ? "ATENDENTE" : "ADMIN"]}
                        </button>
                      )}
                      {member.status === "DESATIVADO" ? (
                        <button
                          type="button"
                          disabled={update.isPending}
                          onClick={() => changeStatus(member, "ATIVO")}
                          className="rounded-full border border-mb-line px-3.5 py-[7px] text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface disabled:opacity-50"
                        >
                          Reativar
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={remove.isPending}
                          onClick={() => {
                            const warning =
                              member.role === "REVENDEDOR"
                                ? `Desativar o revendedor ${member.name}?

A carteira de clientes volta para a empresa: as próximas compras deles não geram comissão. Reativar depois não devolve os clientes.`
                                : `Revogar o acesso de ${member.name} ao painel?`;
                            if (!window.confirm(warning)) return;
                            remove.mutate(member.id, {
                              onSuccess: () => toast.success(`Acesso de ${member.name} revogado.`),
                              onError: (cause) => toast.error(message(cause)),
                            });
                          }}
                          className="rounded-full border border-mb-line px-3.5 py-[7px] text-xs font-bold text-destructive transition-colors hover:border-destructive disabled:opacity-50"
                        >
                          Revogar acesso
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </AdminCard>

        <p className="text-xs text-mb-muted">
          Quem já acessou não é apagado: o acesso é desativado para preservar o rastro de quem mexeu
          no quê. Convites nunca usados são removidos de vez.
        </p>
      </div>
    </>
  );
}

/** Formulario de convite — aparece acima da tabela, sem tirar a lista da vista. */
function InviteForm({
  onClose,
  invite,
}: {
  onClose: () => void;
  invite: ReturnType<typeof useInviteMember>;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("ATENDENTE");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    invite.mutate(
      { name, email, role },
      {
        onSuccess: ({ inviteSent }) => {
          if (inviteSent) toast.success(`Convite enviado para ${email}.`);
          else
            toast.warning(
              `${name} foi convidado, mas o e-mail não saiu. Reenvie ou defina a senha na página da pessoa.`,
            );
          onClose();
        },
        onError: (cause) => toast.error(message(cause)),
      },
    );
  };

  return (
    <AdminCard className="p-6">
      <form onSubmit={handleSubmit}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-bold text-mb-navy">Convidar pessoa</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="text-mb-muted transition-colors hover:text-mb-navy"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-[1fr_1fr_200px_auto] items-end gap-3">
          <label className="block">
            <span className="text-xs font-bold text-mb-ink">Nome</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              minLength={2}
              className="mt-1.5 h-11 w-full rounded-mb-field border border-mb-line px-4 text-sm text-mb-ink outline-none focus:border-mb-navy"
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-mb-ink">E-mail</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              className="mt-1.5 h-11 w-full rounded-mb-field border border-mb-line px-4 text-sm text-mb-ink outline-none focus:border-mb-navy"
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-mb-ink">Papel</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as TeamRole)}
              className="mt-1.5 h-11 w-full rounded-mb-field border border-mb-line bg-white px-3 text-sm text-mb-ink outline-none focus:border-mb-navy"
            >
              <option value="ATENDENTE">Atendente</option>
              <option value="ADMIN">Administrador</option>
              <option value="REVENDEDOR">Revendedor</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={invite.isPending}
            className="flex h-11 items-center gap-2 rounded-mb-button bg-mb-yellow px-5 font-display text-sm font-bold text-mb-ink shadow-cta disabled:opacity-60"
          >
            {invite.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Convidar
          </button>
        </div>

        <p className="mt-3 text-xs text-mb-muted">
          A pessoa recebe um e-mail com o link para criar a própria senha (vale 7 dias). Até lá, a
          conta fica como “convite pendente”.
        </p>
      </form>
    </AdminCard>
  );
}

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : "Não foi possível concluir.";

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
